import clsx from 'clsx';
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAxios from '../../axios/useAxios';
import { PHONE_PASSWORD_LOGIN, PHONE_NUMBER_VERIFY, LOGIN } from '../../constants/routes';
import { auth } from '../../firebase/firebaseConfig';
import {
  handlePhoneNumberSubmission,
  isPhoneValid,
} from '../../utils/phoneNumber';
import PrimaryButton from '../button/PrimaryButton';
import { PhoneInputComponent, normalizePhoneNumber } from './PhoneInput';
import { deployScan } from '../../utils/guestModeTrigger';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import logoGoogleLogin from '../../styles/img/google-icon-logo-svg.svg';
import { useGoogleAuth } from '../../context/GoogleAuthContext';
import { posthogPhoneNumberEvent } from '../../posthog/phoneNumberEvent';
import { POSTHOG_CAPTURE_PHONE_SIGN_IN_SMS, POSTHOG_CAPTURE_PHONE_SIGN_UP_SMS } from '../../posthog/constants';
import { X } from 'lucide-react';
import { cleanupRecaptchaVerifier } from '../../utils/recaptchaService';

export const HomeDrawer = () => {
  const [focused, setFocused] = useState(false);
  const [isShowOtherMethods, setIsShowOtherMethods] = useState(false);
  const { store } = useSelector((state) => state.store);
  const user = useSelector((state) => state.user);
  const location = useLocation();
  const entryQr = location.state?.entryQr || localStorage.getItem('entryQr') || '';
  const cartItems = useSelector((state) => state.cart.cartItems);

  const dispatch = useDispatch();
  const [number, setNumber] = useState('');
  const [countryCode, setCountryCode] = useState('fr');
  const [, setIsOpen] = useState(false);
  const [isValidNumber, setIsValidNumber] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Chargement...');
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const { initiateGoogleLogin } = useGoogleAuth();

  const isButtonOtherMethodClicked = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (focused) {
      setIsOpen(true);
    }
  }, [focused]);

  useEffect(() => {
    setIsValidNumber(isPhoneValid(number));
  }, [number]);

  useEffect(() => {
    const localNumber = localStorage.getItem('phoneNumber');
    if (localNumber) {
      let cleaned = localNumber.startsWith('+') ? localNumber.slice(1) : localNumber;
      const normalizedNumber = normalizePhoneNumber(cleaned, 'fr');

      setNumber(normalizedNumber);

      setTimeout(() => {
        setFocused(true);
      }, 1000);
    }
  }, []);

  const handleNumberChange = (phone, data) => {
    if (data?.countryCode) setCountryCode(data.countryCode);
    setNumber(phone);
  };

  const handleContinue = async () => {
    setFocused(true);
    if (!isValidNumber) return;
    setLoadingLabel('Chargement...');
    const normalizedNumber = normalizePhoneNumber(number, countryCode);
    localStorage.setItem('phoneNumber', normalizedNumber);
    const result = await handlePhoneNumberSubmission({
      phoneNumber: normalizedNumber,
      auth,
      axiosInstance,
      onUserExists: (formattedNumber) => {
        cleanupRecaptchaVerifier();
        posthogPhoneNumberEvent(POSTHOG_CAPTURE_PHONE_SIGN_IN_SMS, { phoneNumber: formattedNumber });
        navigate(PHONE_PASSWORD_LOGIN, {
          state: { phoneNumber: formattedNumber, userExists: true },
        });
      },
      onUserNew: (formattedNumber) => {
        cleanupRecaptchaVerifier();
        posthogPhoneNumberEvent(POSTHOG_CAPTURE_PHONE_SIGN_UP_SMS, { phoneNumber: formattedNumber });
        navigate(PHONE_NUMBER_VERIFY, {
          state: { phoneNumber: formattedNumber, userExists: false },
        });
      },
      onError: (error) => {
        console.error('Phone submission error:', error);
      },
      setLoading: setIsLoading,
      onSmsSending: () => setLoadingLabel('Envoi du SMS...'),
      recaptchaContainerId: 'home-drawer-recaptcha',
    });

    console.info('Phone submission result:', result);
  };

  const handleOtherMethods = () => {
    setIsShowOtherMethods(true);
    isButtonOtherMethodClicked.current = true;
  };

  const handleBlur = () => {
    if (isButtonOtherMethodClicked.current) {
      const inputPhoneNumber = document.querySelector('[name="phone-number-input-drawer"]');
      if (inputPhoneNumber) {
        inputPhoneNumber.blur();

        setTimeout(() => {
          isButtonOtherMethodClicked.current = null;
        }, 500);
      }
    } else if (!isValidNumber) {
      setFocused(false);
    }
  };

  const handleClose = () => {
    setFocused(false);
    setIsOpen(false);
  };

  const handleFocus = () => {
    if (!focused) {
      setFocused(true);
    }
  };

  const renderPhoneAndOtherMethods = () => {
    return (
      <>
        <PrimaryButton
          label={isLoading ? loadingLabel : 'Continuer'}
          onClick={handleContinue}
          disabled={!isValidNumber || isLoading}
          onMouseDown={(event) => event.preventDefault()}
          style={{
            opacity: isValidNumber && !isLoading ? 1 : 0.5,

          }}
        />

        {/*{isShowOtherMethods ?*/}
        {/*  {*/}
        {/*  renderOtherMethods()*/}
        {/*  }*/}
        {/*:*/}
        <button
          type="button"
          className="geomanistMedium -mt-4 text-2xl p-6 underline rounded-full w-full"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => navigate(LOGIN)}
        >
          Autres méthodes
        </button>
        {/*}*/}
      </>
    );
  };

  const renderOtherMethods = () => {
    return (
      <div className="flex flex-col items-center justify-start w-full pt-6 pb-14 gap-10">

        <div className="my-4 h-1 opacity-10 bg-black w-full" />

        <PrimaryButton
          className="!shadow-[0px_4px_0px_0px_rgba(0,0,0,0.1)] "
          style={{
            background: '#ffffff',
            border: '2px solid #E6E6E6',
          }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={initiateGoogleLogin}
        >
          <img src={logoGoogleLogin} alt="google" className="h-10" />
          <span className="ml-3">Google</span>
        </PrimaryButton>

        <PrimaryButton
          label="E-mail"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => navigate(LOGIN)}
          className=" !shadow-[0px_4px_0px_0px_rgba(0,0,0,0.1)] "
          style={{
            background: '#ffffff',
            border: '2px solid #E6E6E6',
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div
        className={clsx(
          'h-[100dvh] w-[100dvw] pointer-events-none fixed top-0 left-0 right-0 bottom-0 z-40 transition-all duration-300',
          focused && 'bg-black opacity-75 pointer-events-auto',
        )}
        onClick={() => {
          setIsOpen(false);
          setFocused(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setIsOpen(false);
            setFocused(false);
          }
        }}
        role="button"
        tabIndex={0}
      />
      <div
        ref={drawerRef}
        className={clsx('w-[100dvw] transition-all duration-500 ease-out bg-white rounded-t-[20px] p-6 px-12 shadow-[0px_2px_40px_0px_rgba(0,0,0,0.3)] ' +
          'fixed bottom-0 left-0 right-0 z-50 border-t-2 border-gray-200' +
          'overflow-y-auto',
        )}
        style={{
          height: focused ? '62dvh' : '170px',
          maxHeight: focused ? '62dvh' : 'none',
        }}
      >
        {focused ? (
          <div className="flex justify-between items-center w-full my-12">
            <h1 className="text-3xl geomanistMedium font-medium text-start">Bienvenue !</h1>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 active:scale-95 transition-all duration-200 ease-out animate-spin-in"
              aria-label="Fermer"
            >
              <X size={16} className="animate-fade-rotate" />
            </button>
          </div>
        ) : null}

        <div className="flex flex-col items-center justify-center gap-8">
          <PhoneInputComponent
            value={number}
            onChange={handleNumberChange}
            defaultCountry="fr"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />

          {focused ? renderPhoneAndOtherMethods()
            :
            <PrimaryButton
              label="Mode invité"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => deployScan({
                store,
                user,
                entryQr,
                dispatch,
                axiosInstance,
                navigate,
                cartItems,
              })}
              className=" !shadow-[0px_4px_0px_0px_rgba(0,0,0,0.1)] "
              style={{
                background: '#ffffff',
                border: '2px solid #E6E6E6',
              }}
            />
          }
        </div>

        {/* Hidden reCAPTCHA container */}
        <div id="home-drawer-recaptcha" style={{ display: 'none' }} />
      </div>
    </>
  );
};
